/**
 * One id per browser tab, sent on every request as `X-Session-Id`.
 *
 * With the signed-in subject it identifies the holder of a customer's edit
 * lease: the same person in two tabs is two holders, and under
 * AUTH_MODE=none (everyone anonymous) the tab is all that tells two people
 * apart. `sessionStorage` survives a reload of the tab, so a refresh keeps
 * the lease, and dies with the tab.
 */
const KEY = "excel-engine.session";

let cached: string | null = null;

function fresh(): string {
  // One binding, so TypeScript does not narrow `crypto` to `never` after the
  // first check: the lib types declare randomUUID as always present.
  const c: Crypto | undefined = typeof crypto === "undefined" ? undefined : crypto;
  if (c?.randomUUID) return c.randomUUID();
  // The fallback used Math.random(), which is predictable: this id is what
  // tells two holders of a customer's edit lease apart, so a guessable one
  // lets a tab claim another's lease. getRandomValues is available wherever
  // this app runs even when randomUUID is not (it predates it by years).
  if (c?.getRandomValues) {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}${performance.now().toString(36).replace(".", "")}`;
}

export function sessionId(): string {
  if (cached) return cached;
  try {
    cached = window.sessionStorage.getItem(KEY);
  } catch {
    cached = null;
  }
  if (!cached) {
    cached = fresh();
    try {
      window.sessionStorage.setItem(KEY, cached);
    } catch {
      /* private mode: the id lives for this page load only */
    }
  }
  return cached;
}
